/** @format */

import { Breadcrumb, Chain, ProductListSEO } from '@shared/data-access/interfaces';

import { CategoryDao } from './category.dao';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PageType } from './page-type.enum';
import { UrlPath } from './url-path.enum';
import { UtilityService } from '../utility.service';
import { map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class CategoryService {
	constructor(
		private chain: Chain,
		private categoryDao: CategoryDao,
		private utilityService: UtilityService
	) {}

	/**
	 * @description
	 * Gets breadcrumbs for a given category ID
	 */
	public getBreadcrumbsByCategoryId$(
		categoryId: string,
		retries?: number
	): Observable<Breadcrumb[]> {
		return this.categoryDao.getBreadcrumbsByCategoryId$(categoryId, retries).pipe(
			map((tempBreadCrumbs: Breadcrumb[]) => {
				const breadcrumbs = Object.assign([], tempBreadCrumbs);

				breadcrumbs.forEach((breadcrumb) => {
					breadcrumb.seoToken = this.utilityService.parseSeoTokenByStore(
						breadcrumb.seo_token_ntk,
						this.chain.storeId,
						this.chain.masterCatalogId
					);
					breadcrumb.url = this.getPageUrl(breadcrumb.dsgpagetype, breadcrumb.seoToken);
				});

				return breadcrumbs;
			})
		);
	}

	/**
	 * @description
	 * Gets a category name by a given ID
	 */
	public getCategoryNameByID$(categoryId: string, retries = 0): Observable<ProductListSEO> {
		return this.categoryDao.getCategoryNameByID$(categoryId, retries);
	}

	/**
	 * @description
	 * Accepts a page type and SEO token and builds a page URL
	 */
	public getPageUrl(pageType: string, seoToken: string): string {
		// Page URL
		let url = '';

		// Check the page type to set the URL path
		if (pageType === PageType.CATEGORY) {
			url += `/${UrlPath.CATEGORY}/`;
		} else if (pageType === PageType.FAMILY || pageType === PageType.SUPER_FAMILY) {
			url += `/${UrlPath.FAMILY}/`;
		} else if (
			pageType === PageType.STATIC ||
			pageType === PageType.SUGGEST ||
			pageType === PageType.NO_SUGGEST ||
			pageType === PageType.NON_TAXO_NO_SUGGEST
		) {
			url += `/${UrlPath.STATIC}/`;
		} else {
			/*
			 * Default to a Family page URL
			 */
			url += `/${UrlPath.FAMILY}/`;
		}

		// Append SEO Token to URL
		url += seoToken;

		// Return the URL
		return url;
	}

	/**
	 * @description
	 * Gets Categories based on seo-token
	 * levels deeper.
	 *
	 * @param seoToken
	 * @param retries
	 * @returns - {Observable<any>}
	 */
	public getCategoryBySeoToken$(seoToken: string, retries?: number): Observable<any> {
		return this.categoryDao.getCategoryBySeoToken$(seoToken, retries).pipe(
			map((tempCategory: any) => {
				// Get a new Object reference
				const categories = Object.assign({}, tempCategory);
				if (categories) {
					// Set the link url
					if (categories.childCatgroups) {
						categories.childCatgroups.forEach((category) => {
							category.expanded = false;
							category.url = this.getPageUrl(
								category.dsgpagetype,
								category.seo_token_ntk
							);
							category.fullImage = this.getimagePath(category.fullImage);
							category.em = `LN_${category.name}`;
							if (
								category.dsgpagetype === PageType.FAMILY ||
								category.dsgpagetype === PageType.SUPER_FAMILY ||
								category.dsgpagetype === null
							) {
								category.hasSubcategories = false;
							}
						});
					}

					return categories;
				}
			})
		);
	}

	/**
	 * @description
	 * Gets subcategories based on array of seoTokens
	 *
	 * @param seoToken
	 * @param retries
	 * @returns - {Observable<any>}
	 */
	public getSubCategoriesBySeoTokens$(seoToken: string, retries?: number): Observable<any> {
		return this.categoryDao.getSubCategoriesBySeoTokens$(seoToken, retries).pipe(
			map((tempSubCategory: any) => {
				const subCategories = tempSubCategory;
				if (subCategories) {
					subCategories.forEach((subCats) => {
						subCats.expanded = false;
						subCats.url = this.getPageUrl(subCats.dsgpagetype, subCats.seo_token_ntk);
						const categoryName = subCats.note ? subCats.note : subCats.name;
						subCats.em = `LN_${categoryName}`;
						if (subCats.childCatgroups) {
							subCats.childCatgroups.forEach((category) => {
								category.url = this.getPageUrl(
									category.dsgpagetype,
									category.seo_token_ntk
								);
								category.fullImage = this.getimagePath(category.fullImage);
								const subcategoryName = category.note
									? category.note
									: category.name;
								category.em = `LN_${categoryName}_${subcategoryName}`;
							});
						}
					});

					return subCategories;
				}
			})
		);
	}

	/**
	 * @description
	 * Accepts fullImage and returns updated imagePath
	 */
	public getimagePath(fullImage) {
		let imgPath = fullImage;
		if (imgPath !== null && !imgPath.includes('www') && !imgPath.includes('scene7')) {
			const img = imgPath.split('://');
			imgPath = `${img[0]}://www.${img[1]}`;
		}
		return imgPath;
	}
}
